/**
 * Turning gestational weeks into calendar dates.
 *
 * The follow-up API dates the pregnancy for us: `GET /cycles/{cycleId}/
 * pregnancy-follow-up` returns how many days along she was on a given day, and
 * the estimated due date, computed from the anchor her care actually used — a
 * transfer, an ovulation date, a clinic due date. This module does the one thing
 * left: say which calendar days a gestational week covers, so a milestone written
 * in weeks can be shown as dates.
 *
 * Pure and clock-free, like `injection-protocol/doseRecord.ts` and for the same
 * reason: the components that would show this reach `react-native` and cannot be
 * loaded by the test runner, and a date that is quietly a week out is not the kind
 * of mistake to discover in a screenshot.
 *
 * Nothing here decides *which* weeks matter. That is clinical content, and it
 * lives — under review — in `earlyPregnancyMilestones.ts`.
 */

/** As the API returns it. Only the fields the arithmetic needs. */
export type GestationSnapshot = {
  /** The day the count was taken, `YYYY-MM-DD`. */
  asOf: string;
  /** Completed days of gestation on `asOf`. */
  gestationalDays: number;
};

/** A calendar window, inclusive at both ends. */
export type CalendarWindow = { start: string; end: string };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

function isoToUtcMs(date: string): number | null {
  if (!ISO_DATE.test(date)) return null;
  const [year, month, day] = date.split("-").map(Number);
  const ms = Date.UTC(year!, month! - 1, day!);
  // `Date.UTC` rolls invalid components over rather than failing — 2026-02-31
  // becomes March 3rd — so the round trip is what rejects them.
  return utcMsToIso(ms) === date ? ms : null;
}

function utcMsToIso(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * A calendar date plus a whole number of days, or null if the date is not one.
 *
 * Shared rather than re-implemented: the same regex, the same `Date.UTC`
 * round-trip and the same day constant had grown a second copy next door, which
 * is two places for the guard to drift out of.
 */
export function addCalendarDays(date: string, days: number): string | null {
  const ms = isoToUtcMs(date);
  if (ms === null) return null;
  return utcMsToIso(ms + days * DAY_MS);
}

/**
 * The day gestational week `week` begins — that is, week `week` + 0 days.
 *
 * Null rather than a guess when the snapshot cannot be read or the week is not a
 * week: a date shown to a pregnant patient is either right or absent.
 */
export function dateOfGestationalWeek(
  gestation: GestationSnapshot | null | undefined,
  week: number,
): string | null {
  if (!gestation) return null;
  if (!Number.isInteger(week) || week < 0) return null;
  if (!Number.isInteger(gestation.gestationalDays)) return null;
  if (gestation.gestationalDays < 0) return null;

  const asOfMs = isoToUtcMs(gestation.asOf);
  if (asOfMs === null) return null;

  return utcMsToIso(asOfMs + (week * 7 - gestation.gestationalDays) * DAY_MS);
}

/**
 * The calendar days a week range covers, inclusive.
 *
 * "Between 6 and 7 weeks" means from 6+0 through 7+6, not through 7+0 — a window
 * that ended on the first day of its last week would cut six days off every
 * milestone it described. A single week (`from === to`) is that week's seven days.
 */
export function gestationalWeekWindow(
  gestation: GestationSnapshot | null | undefined,
  fromWeek: number,
  toWeek: number,
): CalendarWindow | null {
  if (toWeek < fromWeek) return null;

  const start = dateOfGestationalWeek(gestation, fromWeek);
  const lastWeekStart = dateOfGestationalWeek(gestation, toWeek);
  if (!start || !lastWeekStart) return null;

  const lastWeekStartMs = isoToUtcMs(lastWeekStart);
  if (lastWeekStartMs === null) return null;

  return { start, end: utcMsToIso(lastWeekStartMs + 6 * DAY_MS) };
}

/**
 * Whether a window has already gone by on `today`.
 *
 * The caller decides what to do with that — a milestone whose window has passed
 * is not a milestone that failed to happen, and this makes no claim either way.
 */
export function windowHasPassed(
  window: CalendarWindow | null | undefined,
  today: string,
): boolean {
  if (!window) return false;
  if (!ISO_DATE.test(today)) return false;
  return window.end < today;
}

/**
 * The gestation her own report implies.
 *
 * She tells us how many weeks along she is when she records the pregnancy, and
 * that number is the best dating we have: she has it from her clinic, and it is
 * the same field the app already treats as the evidence she announced anything
 * (`summarisePregnancyStatus`). Nothing else — no anchor, no cycle type, no
 * transfer — has to exist for this to work, which is why IUI, ovulation
 * induction and natural conception date exactly as well as IVF here.
 *
 * Two honest limits, and the caller must carry both into what it shows.
 *
 * Whole weeks only. "Six weeks" is anywhere in 6+0 to 6+6, so this reads it as
 * 6+0 — the conservative end — and the result is an approximation. Copy that
 * quotes it should say so.
 *
 * `reportedOn` is when she said it, and the caller supplies it from the record's
 * own timestamp. If that timestamp moves for an unrelated edit, the age computed
 * here moves with it and comes out younger than she is.
 */
export function gestationFromReportedWeeks(params: {
  weekCount: number | null | undefined;
  /** `YYYY-MM-DD`, the day she reported it. */
  reportedOn: string | null | undefined;
  /** Patient-local `YYYY-MM-DD`. */
  today: string;
}): GestationSnapshot | null {
  const { weekCount, reportedOn, today } = params;
  if (weekCount == null || !Number.isInteger(weekCount)) return null;
  // A pregnancy is not 0 weeks when reported and not 45 either. Both ends are
  // data problems rather than pregnancies, and dates built on them would be
  // confidently wrong.
  if (weekCount < 1 || weekCount > 44) return null;
  if (!reportedOn) return null;

  const reportedMs = isoToUtcMs(reportedOn);
  const todayMs = isoToUtcMs(today);
  if (reportedMs === null || todayMs === null) return null;
  // A report dated after today is a clock or a record problem, not a pregnancy
  // that has not started.
  if (todayMs < reportedMs) return null;

  const elapsedDays = Math.round((todayMs - reportedMs) / DAY_MS);
  return {
    asOf: today,
    gestationalDays: weekCount * 7 + elapsedDays,
  };
}
